/* eslint-disable react/require-default-props */
import React from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { colors } from '../../../theme/palette'
import breakpoints from '../../../theme/breakpoints'

// Define Component
type RuleNames = 'text'
interface TypoStyleProps {
    weight?: string
    size?: TypographyVariants
    mobileSize?: TypographyVariants
    color?: string
    spacing?: string
    align?: string
}

export type TypographyVariants =
    | 'XXXXS'
    | 'XXXS'
    | 'XXS'
    | 'XS'
    | 'S'
    | 'M'
    | 'L'
    | 'XL'
    | 'XXL'
    | 'XXXL'
    | 'XXXXL'

const sizes = {
    XXXXS: 0.6875, // 11px
    XXXS: 0.75, // 12px
    XXS: 0.875, // 14px
    XS: 1, // 16px
    S: 1.125,
    M: 1.25,
    L: 1.5,
    XL: 1.75,
    XXL: 2.188,
    XXXL: 2.5,
    XXXXL: 3,
}

const getLineheight = (size: number): number => {
    const suggested = size * 1.3
    return suggested < 3 ? suggested : 3
}

const useStyles = createUseStyles<RuleNames, TypoStyleProps, Jss.Theme>({
    text: {
        // Mobile-first approach: set mobile size as default
        fontSize: ({ size, mobileSize }) => {
            const actualSize = mobileSize || size
            return actualSize ? `${sizes[actualSize]}rem` : '1rem'
        },
        lineHeight: ({ size, mobileSize }) => {
            const actualSize = mobileSize || size
            return `${getLineheight(sizes[actualSize || 'XS'])}rem`
        },

        // Desktop sizes using media query
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            fontSize: ({ size }) => {
                return size ? `${sizes[size]}rem` : '1rem'
            },
            lineHeight: ({ size }) =>
                `${getLineheight(sizes[size || 'XS'])}rem`,
        },

        fontWeight: ({ weight }) => {
            return weight || 'bold'
        },

        fontStyle: 'normal',
        color: ({ color }) => color || colors.black,
        textAlign: ({ align }) => {
            return align || 'left'
        },
        // marginBottom: ({ spacing }) => spacing,
    },
})

const Typography: React.FC<TypoProps> = ({
    tag: Tag = 'p',
    size = 'XXXL',
    mobileSize,
    weight = 'normal',
    color = '#000',
    children,
    spacing = '0.625rem',
    align = 'left',
    ...props
}) => {
    const theme = useTheme<Jss.Theme>()
    const classes = useStyles({
        ...{ size, mobileSize, weight, color, spacing, align },
        theme,
    })

    return (
        <Tag {...props} className={classes.text}>
            {children}
        </Tag>
    )
}

type TypoProps = {
    spacing?: string

    color?: string

    size?: TypographyVariants

    mobileSize?: TypographyVariants

    weight?: 'normal' | 'bold'

    align?: 'left' | 'center' | 'right'

    tag?: keyof JSX.IntrinsicElements
} & React.HTMLAttributes<HTMLOrSVGElement>

const useStylesTitle = createUseStyles({
    title: {
        fontSize: '24px',
        fontWeight: 600,
        margin: 0,
    },
})

interface TitleProps {
    children: React.ReactNode
    style?: React.CSSProperties
}

export const Title: React.FC<TitleProps> = ({ children, style }) => {
    const classes = useStylesTitle()
    return (
        <h2 className={classes.title} style={style}>
            {children}
        </h2>
    )
}

export { Typography }
